import clsx from "clsx";
import Image from "next/image";
import { useTranslations } from "next-intl";

import { DiscBase, DiscCenterButton } from "@ui/components/core/Disc";

// Logo & UI icons
import ElevenLabsLogo from "./assets/voice-changer-pan-demo/elevenlabs-logo.svg";
import CreativePlatformIcon from "./assets/voice-changer-pan-demo/creative-platform.svg";
import ChevronUpDownIcon from "./assets/voice-changer-pan-demo/chevron-up-down.svg";
import PlusIcon from "./assets/voice-changer-pan-demo/plus.svg";
import MicIcon from "./assets/voice-changer-pan-demo/mic.svg";
import SearchIcon from "./assets/voice-changer-pan-demo/search.svg";

// Sidebar icons
import HomeIcon from "./assets/voice-changer-pan-demo/sidebar-icons/home.svg";
import VoicesIcon from "./assets/voice-changer-pan-demo/sidebar-icons/voices.svg";
import SpeechIcon from "./assets/voice-changer-pan-demo/sidebar-icons/speech.svg";
import VoiceChangerIcon from "./assets/voice-changer-pan-demo/sidebar-icons/voice_changer.svg";
import MusicIcon from "./assets/voice-changer-pan-demo/sidebar-icons/music.svg";
import SfxIcon from "./assets/voice-changer-pan-demo/sidebar-icons/sfx.svg";
import ImageAndVideoIcon from "./assets/voice-changer-pan-demo/sidebar-icons/image_and_video.svg";
import WorkflowsIcon from "./assets/voice-changer-pan-demo/sidebar-icons/workflows.svg";
import StudioIcon from "./assets/voice-changer-pan-demo/sidebar-icons/studio.svg";
import DubbingIcon from "./assets/voice-changer-pan-demo/sidebar-icons/dubbing.svg";
import ProductionsIcon from "./assets/voice-changer-pan-demo/sidebar-icons/productions.svg";
import TranscriptionIcon from "./assets/voice-changer-pan-demo/sidebar-icons/transcription.svg";

// Voice avatar images
import imgVoiceBg01 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 01.png";
import imgVoiceBg02 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 02.png";
import imgVoiceBg03 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 03.png";
import imgVoiceBg04 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 04.png";
import imgVoiceBg05 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 05.png";
import imgVoiceBg06 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 06.png";
import imgVoiceBg07 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 07.png";
import imgVoiceBg08 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 08.png";
import { EGGSHELL } from "@ui/tokens/pageColors";

const VOICE_AVATARS = [
  imgVoiceBg01,
  imgVoiceBg02,
  imgVoiceBg03,
  imgVoiceBg04,
  imgVoiceBg05,
  imgVoiceBg06,
  imgVoiceBg07,
  imgVoiceBg08,
];

// Voice data for the selection panel (intentionally not localized)

const VOICE_OPTIONS = [
  { id: 1, name: "Burt Reynolds", description: "Elevenlabs' Iconic Voice", isSelected: true },
  {
    id: 2,
    name: "Enrique Montragón",
    description: "Middle aged male with a Mexican Spanish accent",
    isSelected: false,
  },
  { id: 3, name: "Finn", description: "A well-connected, young conversational male", isSelected: false },
  { id: 4, name: "Hope", description: "A warm, soothing, and captivating voice", isSelected: false },
  { id: 5, name: "Magic Jack", description: "Casual, friendly, heart-felt voice", isSelected: false },
  { id: 6, name: "Jack Bass", description: "A calm and gritty storyteller", isSelected: false },
  { id: 7, name: "Jarnathan", description: "A warm, smooth, and versatile voice", isSelected: false },
  { id: 8, name: "Lean Stern", description: "A rich, deep voice for fantasy", isSelected: false },
];

// Navigation item component
function NavItem({
  icon,
  label,
  isActive = false,
  isOpaque = true,
}: {
  icon: React.ReactNode;
  label: string;
  isActive?: boolean;
  isOpaque?: boolean;
}) {
  return (
    <div
      className={clsx(
        "tw-flex tw-items-center tw-gap-1 tw-h-5 sm:tw-h-[1.375rem] lg:tw-h-6 tw-px-1.5 tw-rounded-md tw-text-[0.5625rem] sm:tw-text-[0.625rem] lg:tw-text-[0.6875rem] tw-font-medium tw-leading-none",
        isActive ? "bg-dark-new" : "tw-bg-transparent",
        !isOpaque && !isActive && "tw-opacity-60",
      )}
    >
      <div className='tw-size-3 sm:tw-size-3.5 lg:tw-size-4 tw-flex tw-items-center tw-justify-center tw-text-black tw-shrink-0'>
        {icon}
      </div>
      <span className='tw-text-black'>{label}</span>
    </div>
  );
}

// Section label component
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className='tw-px-1.5 tw-text-[0.5rem] sm:tw-text-[0.5625rem] lg:tw-text-[0.625rem] tw-font-medium tw-text-black tw-opacity-60 tw-leading-none tw-mb-0.5'>
      {children}
    </div>
  );
}

// Voice item component for the selection panel
function VoiceItem({
  name,
  description,
  isSelected = false,
  index,
}: {
  name: string;
  description: string;
  isSelected?: boolean;
  index: number;
}) {
  const avatarImage = VOICE_AVATARS[index % VOICE_AVATARS.length];

  return (
    <div
      className={clsx(
        "tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-py-1.5 sm:tw-py-2 lg:tw-py-2.5 tw-px-2 sm:tw-px-[0.625rem] lg:tw-px-3",
        isSelected ? "bg-dark-new" : "tw-bg-transparent",
      )}
    >
      <div className='tw-flex tw-items-center tw-gap-1.5 sm:tw-gap-2 lg:tw-gap-2.5 tw-min-w-0 tw-flex-1'>
        <Image
          src={avatarImage}
          alt={name}
          className='tw-size-5 sm:tw-size-6 lg:tw-size-7 tw-rounded-full tw-object-cover tw-flex-shrink-0'
        />
        <div className='tw-flex tw-flex-col tw-min-w-0'>
          <span
            className='tw-text-[0.5625rem] sm:tw-text-[0.625rem] lg:tw-text-[0.6875rem] tw-text-black'
            style={{ lineHeight: 1.4 }}
          >
            {name}
          </span>
          <span
            className='tw-text-[0.5rem] sm:tw-text-[0.5625rem] lg:tw-text-[0.625rem] tw-text-black/60 tw-truncate'
            style={{ lineHeight: 1.2 }}
          >
            {description}
          </span>
        </div>
      </div>
      <div
        className={clsx(
          "tw-size-2.5 sm:tw-size-3 lg:tw-size-3.5 tw-text-black tw-shrink-0",
          isSelected ? "tw-opacity-40" : "tw-opacity-0",
        )}
      >
        <PlusIcon className='tw-size-full' />
      </div>
    </div>
  );
}

// Voice selection panel
function VoiceSelectionPanel() {
  const t = useTranslations("ClientKeys.MockDashboards.VoiceChanger");

  return (
    <div className='tw-border-l tw-border-black/5 tw-w-[15rem] sm:tw-w-[17.5rem] md:tw-w-[20rem] lg:tw-w-[22.5rem] tw-flex tw-flex-col tw-px-2 sm:tw-px-3 lg:tw-px-4 tw-py-4 sm:tw-py-5 lg:tw-py-6'>
      <span className='tw-text-[0.6875rem] sm:tw-text-[0.75rem] lg:tw-text-[0.8125rem] tw-text-black tw-leading-none'>
        {t("select_a_voice")}
      </span>
      <div className='tw-flex tw-items-center tw-gap-1 tw-h-6 sm:tw-h-7 lg:tw-h-8 tw-px-2 tw-py-0.5 tw-mt-3 sm:tw-mt-4 tw-rounded-lg tw-bg-white/50 tw-border tw-border-black/10'>
        <SearchIcon className='tw-size-2.5 sm:tw-size-3 lg:tw-size-3 tw-text-black/40 tw-shrink-0' />
        <span className='tw-text-[0.5rem] sm:tw-text-[0.5625rem] lg:tw-text-[0.625rem] tw-text-black/40'>
          {t("search")}
        </span>
      </div>
      <div className='tw-flex tw-flex-col tw-mt-1.5 sm:tw-mt-2'>
        {VOICE_OPTIONS.map((voice, index) => (
          <VoiceItem
            key={voice.id}
            name={voice.name}
            description={voice.description}
            isSelected={voice.isSelected}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}

// Sidebar component
function Sidebar() {
  const t = useTranslations("ClientKeys.MockDashboards.VoiceChanger");

  return (
    <div className='tw-w-40 sm:tw-w-44 md:tw-w-48 lg:tw-w-52 tw-flex-shrink-0 tw-border-r tw-border-black/5 tw-p-2 sm:tw-p-3 md:tw-p-4 tw-flex tw-flex-col tw-gap-2 sm:tw-gap-3 md:tw-gap-4'>
      {/* Logo */}
      <ElevenLabsLogo className='tw-w-16 tw-h-auto tw-shrink-0 tw-mb-1 sm:tw-mb-2' />

      {/* Platform Selector */}
      <div
        className='tw-flex tw-items-center tw-justify-between tw-px-1.5 tw-h-5 sm:tw-h-[1.375rem] lg:tw-h-6 tw-rounded-md tw-bg-white'
        style={{
          boxShadow:
            "0 0 0.0625rem rgba(0,0,0,0.4), 0 0.0625rem 0.125rem rgba(0,0,0,0.04), 0 0.125rem 0.125rem rgba(0,0,0,0.04)",
        }}
      >
        <div className='tw-flex tw-items-center tw-gap-1.5'>
          <CreativePlatformIcon className='tw-size-3 sm:tw-size-3.5 lg:tw-size-4 tw-shrink-0' />
          <span className='tw-text-[0.5rem] sm:tw-text-[0.5625rem] lg:tw-text-[0.625rem] tw-font-medium tw-text-gray-950 tw-whitespace-nowrap'>
            {t("platform_selector")}
          </span>
        </div>
        <ChevronUpDownIcon className='tw-size-2 sm:tw-size-2.5 lg:tw-size-3 tw-text-gray-500 tw-shrink-0' />
      </div>

      {/* Main Navigation */}
      <div className='tw-flex tw-flex-col tw-gap-0.5'>
        <NavItem icon={<HomeIcon />} label={t("nav_home")} isOpaque={false} />
        <NavItem icon={<VoicesIcon />} label={t("nav_voices")} isOpaque={false} />
      </div>

      {/* Generate Section */}
      <div className='tw-flex tw-flex-col tw-gap-0.5'>
        <SectionLabel>{t("section_generate")}</SectionLabel>
        <NavItem icon={<SpeechIcon />} label={t("nav_speech")} isOpaque={false} />
        <NavItem icon={<VoiceChangerIcon />} label={t("nav_voice_changer")} isActive />
        <NavItem icon={<MusicIcon />} label={t("nav_music")} isOpaque={false} />
        <NavItem icon={<SfxIcon />} label={t("nav_sfx")} isOpaque={false} />
        <NavItem icon={<ImageAndVideoIcon />} label={t("nav_image_and_video")} isOpaque={false} />
        <NavItem icon={<WorkflowsIcon />} label={t("nav_workflows")} isOpaque={false} />
      </div>

      {/* Products Section */}
      <div className='tw-flex tw-flex-col tw-gap-0.5'>
        <SectionLabel>{t("section_products")}</SectionLabel>
        <NavItem
          icon={<StudioIcon style={{ width: "70%", height: "70%" }} />}
          label={t("nav_studio")}
          isOpaque={false}
        />
        <NavItem icon={<DubbingIcon />} label={t("nav_dubbing")} isOpaque={false} />
        <NavItem icon={<ProductionsIcon />} label={t("nav_productions")} isOpaque={false} />
        <NavItem
          icon={<TranscriptionIcon style={{ width: "70%", height: "70%" }} />}
          label={t("nav_transcription")}
          isOpaque={false}
        />
      </div>
    </div>
  );
}

// Disc area (main content area)
function DiscArea() {
  const t = useTranslations("ClientKeys.MockDashboards.VoiceChanger");

  return (
    <div className='tw-flex-1 tw-w-[12.5rem] sm:tw-w-[17.5rem] md:tw-w-[20.625rem] lg:tw-w-[22.5rem] tw-flex tw-flex-col tw-pt-4 sm:tw-pt-6 md:tw-pt-8'>
      <span className='tw-text-[0.6875rem] sm:tw-text-[0.75rem] lg:tw-text-[0.875rem] tw-font-normal tw-text-black tw-pl-4 sm:tw-pl-6 md:tw-pl-8'>
        {t("title")}
      </span>
      <div className='tw-flex tw-flex-col tw-items-center tw-gap-3 sm:tw-gap-4 md:tw-gap-5 tw-mt-8 sm:tw-mt-12 md:tw-mt-16 tw-px-4 sm:tw-px-6 md:tw-px-8'>
        <div
          className='tw-size-24 sm:tw-size-28 md:tw-size-32 lg:tw-size-36 tw-rounded-full tw-border-black/10'
          style={{ borderWidth: "0.03125rem" }}
        >
          <DiscBase
            noiseOpacity={0}
            showShadow={false}
            colors={{
              primary: EGGSHELL,
              secondary: EGGSHELL,
              accent: EGGSHELL,
              highlight: EGGSHELL,
            }}
          >
            <DiscCenterButton
              icon={<MicIcon className='tw-size-2.5 sm:tw-size-3 lg:tw-size-3.5 tw-shrink-0' />}
              showShadow={true}
              className='tw-bg-white !tw-size-6 sm:!tw-size-7 md:!tw-size-8 lg:!tw-size-9'
            />
          </DiscBase>
        </div>
        <div className='tw-flex tw-flex-col tw-items-center'>
          <div
            className='tw-text-[0.5625rem] sm:tw-text-[0.625rem] lg:tw-text-[0.6875rem] tw-font-medium tw-text-black'
            style={{ lineHeight: 1.4 }}
          >
            {t("record_or_upload")}
          </div>
          <div
            className='tw-text-[0.5rem] sm:tw-text-[0.5625rem] lg:tw-text-[0.625rem] tw-font-normal tw-text-black/60 tw-mt-0.5'
            style={{ lineHeight: 1.4 }}
          >
            {t("file_size_limit")}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * VoiceChangerMockDashboard - Pure UI component for the Voice Changer dashboard
 * This is a presentational component without any animation or BlockStickyContentList logic.
 */
export function VoiceChangerMockDashboard() {
  return (
    <div className='tw-h-full tw-flex tw-rounded-xl sm:tw-rounded-2xl tw-overflow-hidden'>
      <Sidebar />
      <DiscArea />
      <VoiceSelectionPanel />
    </div>
  );
}
